"use client";

import React, { useState, useEffect } from "react";
import { usePrivy, useWallets } from "@privy-io/react-auth";
import { useDeviceAndWalletDetection } from "@/hooks/useDeviceAndWalletDetection";
import { usePrivyWallet } from "@/hooks/usePrivyWallet";
import Image from "next/image";
import { CheckCircle, AlertCircle, Loader2 } from "lucide-react";
import { MYGEOTOKENS_WALLET_DISPLAY_NAME } from "@/constants/wallet-branding";

function linkedWalletLabel(walletClientType: string | undefined) {
	if (walletClientType === "privy" || walletClientType === "phantom") {
		return MYGEOTOKENS_WALLET_DISPLAY_NAME;
	}
	return walletClientType ?? "Wallet";
}

interface PhantomWalletLinkerProps {
	onSuccess?: () => void;
	onCancel?: () => void;
}

/**
 * Custom Phantom wallet linking component
 * Allows users to connect Phantom extension to their existing Privy account
 */
export function PhantomWalletLinker({
	onSuccess,
	onCancel,
}: PhantomWalletLinkerProps) {
	const { user, authenticated } = usePrivy();
	const { wallets } = useWallets();
	const { connect } = usePrivyWallet();
	const { isMobile, hasPhantomExtension } = useDeviceAndWalletDetection();

	const [isLinking, setIsLinking] = useState(false);
	const [linkError, setLinkError] = useState<string | null>(null);
	const [linkSuccess, setLinkSuccess] = useState(false);
	const [manualPhantomCheck, setManualPhantomCheck] = useState(false);
	const [isCheckingPhantom, setIsCheckingPhantom] = useState(false);

	// Check if user already has Phantom wallet linked
	const existingPhantomWallet = wallets.find(
		(wallet) => wallet.walletClientType === "phantom"
	);

	// Check if user has any Solana wallets
	const existingSolanaWallets = wallets.filter(
		(wallet) =>
			wallet.walletClientType === "phantom" ||
			wallet.walletClientType === "privy"
	);

	// Manual phantom detection function
	const checkPhantomManually = async () => {
		setIsCheckingPhantom(true);
		setLinkError(null);

		try {
			// Wait a bit for extension to load if it's still loading
			await new Promise((resolve) => setTimeout(resolve, 500));

			const phantom = (window as any).solana;
			const isPhantomAvailable = !!(phantom && phantom.isPhantom);
			if (isPhantomAvailable) {
				setManualPhantomCheck(true);
				setLinkError(null);
			} else {
				setLinkError(
					`${MYGEOTOKENS_WALLET_DISPLAY_NAME} browser extension still not detected. Please ensure it's installed and refresh the page.`
				);
			}
		} catch (error) {
			console.error(" Manual phantom check error:", error);
			setLinkError(
				`Error checking for ${MYGEOTOKENS_WALLET_DISPLAY_NAME} browser extension.`
			);
		} finally {
			setIsCheckingPhantom(false);
		}
	};

	const handleLinkPhantom = async () => {
		if (!hasPhantomExtension && !manualPhantomCheck) {
			setLinkError(
				`${MYGEOTOKENS_WALLET_DISPLAY_NAME} browser extension not detected. Please install it first.`
			);
			return;
		}

		if (existingPhantomWallet) {
			setLinkError(
				`${MYGEOTOKENS_WALLET_DISPLAY_NAME} is already linked to your account.`
			);
			return;
		}

		setIsLinking(true);
		setLinkError(null);

		try {
			// First verify Phantom is available
			const phantom = (window as any).solana;
			if (!phantom || !phantom.isPhantom) {
				throw new Error(
					`${MYGEOTOKENS_WALLET_DISPLAY_NAME} browser extension not found. Please ensure it's installed and enabled.`
				);
			}
			// Try to connect via Privy system directly
			await connect("phantom");
			setLinkSuccess(true);

			// Call success callback after a short delay
			setTimeout(() => {
				onSuccess?.();
			}, 1500);
		} catch (error: any) {
			const raw: string = error?.message ?? error?.error ?? String(error ?? "");
			const isAlreadyLinked =
				raw.includes("linked_to_another_user") ||
				error?.code === "linked_to_another_user";

			let errorMessage = isAlreadyLinked
				? "This wallet is already registered to another account. Go to your Profile page and use the Merge Accounts option to combine them."
				: raw.includes("not found")
				? `${MYGEOTOKENS_WALLET_DISPLAY_NAME} browser extension not detected. Please install it and refresh the page.`
				: raw.includes("rejected") || raw.includes("denied")
				? `Connection was rejected. Please approve the connection in ${MYGEOTOKENS_WALLET_DISPLAY_NAME} and try again.`
				: raw.includes("timeout")
				? `Connection timed out. Please ensure ${MYGEOTOKENS_WALLET_DISPLAY_NAME} is unlocked and try again.`
				: raw.includes("cancelled") || raw.includes("closed")
				? ""
				: `Failed to link ${MYGEOTOKENS_WALLET_DISPLAY_NAME}. Please try again.`;

			if (errorMessage) setLinkError(errorMessage);
		} finally {
			setIsLinking(false);
		}
	};

	// Don't show on mobile
	if (isMobile) {
		return (
			<div className="bg-[#1A1A1A] rounded-xl p-6 text-center">
				<AlertCircle className="w-12 h-12 text-yellow-500 mx-auto mb-4" />
				<h3 className="text-lg font-semibold text-white mb-2">
					Browser extension not available
				</h3>
				<p className="text-gray-400 text-sm mb-4">
					Linking {MYGEOTOKENS_WALLET_DISPLAY_NAME} from a browser extension is
					only available on desktop devices.
				</p>
				<button
					onClick={onCancel}
					className="bg-gray-600 hover:bg-gray-700 text-white px-4 py-2 rounded-lg transition-colors"
				>
					Go Back
				</button>
			</div>
		);
	}

	// Don't show if Phantom extension not detected (unless manual check passed)
	if (!hasPhantomExtension && !manualPhantomCheck) {
		return (
			<div className="bg-[#1A1A1A] rounded-xl p-6 text-center">
				<AlertCircle className="w-12 h-12 text-yellow-500 mx-auto mb-4" />
				<h3 className="text-lg font-semibold text-white mb-2">
					Browser extension required
				</h3>
				<p className="text-gray-400 text-sm mb-4">
					Please install the {MYGEOTOKENS_WALLET_DISPLAY_NAME} browser extension
					to link your wallet.
				</p>

				{/* Error message for manual check */}
				{linkError && (
					<div className="bg-red-500/20 border border-red-500/30 rounded-lg p-3 mb-4">
						<p className="text-red-400 text-sm">{linkError}</p>
					</div>
				)}

				<div className="space-y-2">
					<a
						href="https://phantom.app/download"
						target="_blank"
						rel="noopener noreferrer"
						className="block bg-purple-600 hover:bg-purple-700 text-white px-4 py-2 rounded-lg transition-colors text-center"
					>
						Get {MYGEOTOKENS_WALLET_DISPLAY_NAME}
					</a>

					{/* Manual check button */}
					<button
						onClick={checkPhantomManually}
						disabled={isCheckingPhantom}
						className="w-full bg-blue-600 hover:bg-blue-700 disabled:bg-blue-600/50 text-white px-4 py-2 rounded-lg transition-colors flex items-center justify-center gap-2"
					>
						{isCheckingPhantom ? (
							<>
								<Loader2 className="w-4 h-4 animate-spin" />
								Checking for extension...
							</>
						) : (
							<>I have the extension installed — check again</>
						)}
					</button>

					<button
						onClick={onCancel}
						className="w-full bg-gray-600 hover:bg-gray-700 text-white px-4 py-2 rounded-lg transition-colors"
					>
						Go Back
					</button>
				</div>

				{/* Helpful instructions */}
				<div className="mt-4 bg-blue-500/20 border border-blue-500/30 rounded-lg p-3">
					<p className="text-blue-400 text-xs">
						If the extension is installed but not detected, try refreshing the
						page or click Check again after ensuring the extension is enabled.
					</p>
				</div>
			</div>
		);
	}

	// Show success state
	if (linkSuccess) {
		return (
			<div className="bg-[#1A1A1A] rounded-xl p-6 text-center">
				<CheckCircle className="w-12 h-12 text-green-500 mx-auto mb-4" />
				<h3 className="text-lg font-semibold text-white mb-2">
					{MYGEOTOKENS_WALLET_DISPLAY_NAME} linked
				</h3>
				<p className="text-gray-400 text-sm mb-4">
					Your {MYGEOTOKENS_WALLET_DISPLAY_NAME} has been successfully linked to
					your account.
				</p>
				<div className="bg-green-500/20 border border-green-500/30 rounded-lg p-3 mb-4">
					<p className="text-green-400 text-xs">
						You can now switch between your in-app wallet and browser extension
						wallet.
					</p>
				</div>
			</div>
		);
	}

	// Show already linked state
	if (existingPhantomWallet) {
		return (
			<div className="bg-[#1A1A1A] rounded-xl p-6 text-center">
				<CheckCircle className="w-12 h-12 text-green-500 mx-auto mb-4" />
				<h3 className="text-lg font-semibold text-white mb-2">
					Already linked
				</h3>
				<p className="text-gray-400 text-sm mb-4">
					Your {MYGEOTOKENS_WALLET_DISPLAY_NAME} browser wallet is already
					connected to this account.
				</p>
				<div className="bg-green-500/20 border border-green-500/30 rounded-lg p-3 mb-4">
					<p className="text-green-400 text-xs">
						Address: {existingPhantomWallet.address?.slice(0, 8)}...
						{existingPhantomWallet.address?.slice(-8)}
					</p>
				</div>
				<button
					onClick={onSuccess}
					className="bg-green-600 hover:bg-green-700 text-white px-4 py-2 rounded-lg transition-colors"
				>
					Continue
				</button>
			</div>
		);
	}

	return (
		<div className="bg-[#1A1A1A] rounded-xl p-6 text-center">
			<div className="w-16 h-16 bg-purple-600 rounded-full flex items-center justify-center mx-auto mb-4">
				<Image
					src="/phantom-icon.svg"
					alt={MYGEOTOKENS_WALLET_DISPLAY_NAME}
					width={32}
					height={32}
					className="phantom-icon"
				/>
			</div>

			<h3 className="text-xl font-semibold text-white mb-2">
				Link {MYGEOTOKENS_WALLET_DISPLAY_NAME}
			</h3>

			<p className="text-gray-400 text-sm mb-6">
				Connect {MYGEOTOKENS_WALLET_DISPLAY_NAME} to your existing account to
				switch between wallets.
			</p>

			{/* Show existing wallets */}
			{existingSolanaWallets.length > 0 && (
				<div className="bg-gray-800 rounded-lg p-4 mb-6">
					<p className="text-white text-sm font-medium mb-2">
						Current Wallets:
					</p>
					{existingSolanaWallets.map((wallet, index) => (
						<div
							key={index}
							className="flex items-center gap-2 text-xs text-gray-300 mb-1"
						>
							<div className="w-2 h-2 bg-green-500 rounded-full"></div>
							<span>
								{linkedWalletLabel(wallet.walletClientType)}:
								{wallet.address?.slice(0, 8)}...{wallet.address?.slice(-8)}
							</span>
						</div>
					))}
				</div>
			)}

			{/* Error message */}
			{linkError && (
				<div className="bg-red-500/20 border border-red-500/30 rounded-lg p-3 mb-4">
					<p className="text-red-400 text-sm">{linkError}</p>
				</div>
			)}

			{/* Action buttons */}
			<div className="space-y-3">
				<button
					onClick={handleLinkPhantom}
					disabled={isLinking}
					className="w-full bg-purple-600 hover:bg-purple-700 disabled:bg-purple-600/50 py-3 px-6 rounded-xl text-white font-medium transition-colors flex items-center justify-center gap-2"
				>
					{isLinking ? (
						<>
							<Loader2 className="w-4 h-4 animate-spin" />
							Linking wallet...
						</>
					) : (
						<>
							<Image
								src="/phantom-icon.svg"
								alt={MYGEOTOKENS_WALLET_DISPLAY_NAME}
								width={16}
								height={16}
								className="phantom-icon"
							/>
							Link {MYGEOTOKENS_WALLET_DISPLAY_NAME}
						</>
					)}
				</button>

				<button
					onClick={onCancel}
					disabled={isLinking}
					className="w-full bg-gray-600 hover:bg-gray-700 disabled:bg-gray-600/50 text-white px-4 py-2 rounded-lg transition-colors"
				>
					Cancel
				</button>
			</div>

			{/* Info text */}
			<div className="mt-4 bg-blue-500/20 border border-blue-500/30 rounded-lg p-3">
				<p className="text-blue-400 text-xs">
					After linking, you will be able to switch between your in-app wallet
					and browser extension on desktop devices.
				</p>
			</div>
		</div>
	);
}
